import { ChatbotService } from "gen/clients/llts";

/**
 * Obtains and caches the session token that authorises this browser against chatkit-server.
 *
 * llts-back is the access-control point: its session endpoints run the feature-flag check and
 * mint a short-lived token. chatkit-server verifies that token and nothing else, so refreshing it
 * is entirely the client's responsibility.
 *
 * ChatKit's self-hosted mode has no session-refresh hook - unlike the hosted `getClientSecret`
 * callback it replaces - so the token is re-minted on the first 401 instead.
 */

let cachedToken: string | null = null;
let inFlight: Promise<string> | null = null;

/**
 * Fetches a session token, reusing the cached one unless a refresh is forced.
 * Concurrent callers share a single request so a burst of ChatKit calls mints one token.
 * @param isAuthenticated whether a user is signed in; selects which endpoint issues the token
 * @param forceRefresh true to discard the cached token and mint a new one
 */
export const getSessionToken = async (isAuthenticated: boolean, forceRefresh = false): Promise<string> => {
  if (cachedToken && !forceRefresh) {
    return cachedToken;
  }
  if (!inFlight) {
    inFlight = (
      isAuthenticated
        ? ChatbotService.crateAuthenticatedChatbotSession()
        : ChatbotService.crateUnauthenticatedChatbotSession()
    )
      .then(response => {
        cachedToken = response.sessionSecret;
        return cachedToken;
      })
      .finally(() => {
        inFlight = null;
      });
  }
  return inFlight;
};

/** Drops the cached token. Call on sign-in or sign-out so identity does not leak across sessions. */
export const clearSessionToken = (): void => {
  cachedToken = null;
};

/**
 * Builds the fetch override ChatKit uses for every request to chatkit-server.
 *
 * Note this runs inside the ChatKit iframe rather than on our page, so the returned closure must
 * stay free of anything that cannot cross that boundary.
 * @param isAuthenticated whether a user is signed in
 */
export const createChatKitFetch =
  (isAuthenticated: boolean) =>
  async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    // Buffer the body so the request can be replayed after a token refresh; a stream body would
    // already be consumed by the first attempt.
    const body = init?.body;
    const withAuth = async (token: string): Promise<Response> => {
      const headers = new Headers(init?.headers);
      headers.set("Authorization", `Bearer ${token}`);
      return fetch(input, { ...init, headers, body });
    };

    const response = await withAuth(await getSessionToken(isAuthenticated));
    if (response.status !== 401) {
      return response;
    }
    // The token expired under a chat window left open. Mint a new one and replay once.
    return withAuth(await getSessionToken(isAuthenticated, true));
  };
